// ── 상황판 큰 카드 공용 본문 컴포넌트 (2026-06-07 큰 카드 패턴 적용) ─────────
// SbContent 분리 — 표/리스트/통계 컬럼 등 본문 빌더만 모음.
// 큰 카드 패턴(흰 배경 위) 에 맞춰 폰트/배경 조정.

import React from 'react';
import { cn } from '../../../../lib/utils';
import { formatRegion } from '../utils';
import { UnifiedCard } from '../UnifiedCard';
import { accentText } from '../widgetAccent';

/** KPI 카드 — S 사이즈 디자이너 풍 (2026-06-07).
 *  라벨 상단 / 큰 숫자 중앙 (text-3xl font-black accent) / 부가 정보 하단 / watermark.
 *  watermarkIcon prop 으로 우측 하단 watermark 전달.
 */
// onClick      : 숫자 탭 → 상세 모달(drill-down)
// onCardClick  : 카드 빈곳 탭 → 연결 메뉴 navigate (사용자 명세 2026-06-18)
export function renderKpi({ label, value, accent, accentOverride, onClick, onCardClick, sortableId, pulse, code, watermarkIcon }) {
  const tone = accentOverride || accent;
  return (
    <UnifiedCard
      key={code}
      code={code}
      title={label}
      accent={accent}
      accentOverride={accentOverride}
      size="S"
      onClick={onCardClick}
      sortableId={sortableId}
      pulse={pulse}
      watermarkIcon={watermarkIcon}
    >
      <div className="flex flex-col items-end justify-center h-full">
        {/* 숫자 = drill-down. 카드 navigate 와 분리하기 위해 클릭/포인터다운 stopPropagation */}
        {onClick ? (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onClick(e); }}
            onPointerDown={(e) => e.stopPropagation()}
            className={cn('text-3xl font-black tabular-nums leading-none cursor-pointer bg-transparent border-0 p-0', accentText(tone))}
          >
            {value}
          </button>
        ) : (
          <div className={cn('text-3xl font-black tabular-nums leading-none', accentText(tone))}>
            {value}
          </div>
        )}
        {onClick && (
          <div className="text-[10px] mt-2 font-semibold opacity-70 text-[var(--text-sub)]">
            숫자 탭=상세
          </div>
        )}
      </div>
    </UnifiedCard>
  );
}

/** 3컬럼 통계 박스 (StatBox/StatTile 대체) — 흰 카드 위 옅은 배경 셀.
 *  emphasis='lg' 옵션: 값 폰트 text-2xl (M 사이즈 큰 강조).
 *  기본 text-xs.
 */
export function StatColumns({ items, emphasis }) {
  const valueClass = emphasis === 'lg'
    ? 'text-2xl font-black tabular-nums leading-none'
    : 'text-xs font-black tabular-nums leading-none';
  return (
    <div className="grid grid-cols-3 gap-2">
      {items.map((it) => (
        <div
          key={it.label}
          onClick={it.onClick ? (e) => { e.stopPropagation(); it.onClick(e); } : undefined}
          onPointerDown={it.onClick ? (e) => e.stopPropagation() : undefined}
          className={cn(
            'rounded-lg p-1.5 flex flex-col gap-0.5 min-h-[54px] items-center justify-center',
            it.onClick && 'cursor-pointer hover:-translate-y-px transition-transform',
            it.danger
              ? 'bg-red-50 border border-red-200 dark:bg-red-900/20 dark:border-red-700'
              : 'bg-slate-50 dark:bg-pine-800/40 border border-slate-200 dark:border-pine-700',
          )}
        >
          <div className="text-[10px] font-bold text-[var(--text-sub)] uppercase tracking-wider">{it.label}</div>
          <div className={valueClass} style={{ color: it.color }}>{it.value}</div>
        </div>
      ))}
    </div>
  );
}

/** 이름 칩 그룹 (NamePills 대체) — 작은 chip 으로 20명+ 도 한눈에 보이게 (2026-06-09 압축). */
export function NamePills({ names }) {
  return (
    <div className="flex flex-wrap gap-0.5">
      {names.map((name) => (
        <span key={name} className="bg-slate-50 dark:bg-pine-800 border border-slate-200 dark:border-pine-700 rounded px-1 py-0 text-[9px] font-bold text-[var(--text)] whitespace-nowrap">{name}</span>
      ))}
    </div>
  );
}

/** 방문 일정 행 (MeetingRow 대체) — TM 오늘 콜 미팅 list 동일 패턴 (text-[10px] text-muted).
 *  큰 카드 본문, 옅은 슬레이트 배경. 2 라인 세로 정리 (글씨 잘림 방지). */
export function MeetingRowItem({ item }) {
  return (
    <div className="flex flex-col gap-0.5 px-2 py-1.5 bg-slate-50 dark:bg-pine-800/40 rounded-lg border-l-2 border-indigo-400 overflow-hidden min-w-0">
      <div className="flex items-center gap-1 min-w-0">
        <span className="text-[10px] text-[var(--text-muted)] truncate flex-shrink-0">{item.sender}</span>
        <span className="text-[10px] text-[var(--text-muted)] flex-shrink-0">→</span>
        <span className="text-[10px] font-bold text-[var(--text-muted)] truncate flex-1">{item.marketer}</span>
      </div>
      <div className="flex items-baseline justify-between gap-1 min-w-0">
        <span className="text-[10px] text-[var(--text-muted)] truncate flex-1">{formatRegion(item.region)}</span>
        <span className="text-[10px] font-black text-[var(--text-muted)] whitespace-nowrap flex-shrink-0">{item.count}건</span>
      </div>
    </div>
  );
}
